// packages/frontend/src/stores/authStore.ts
import { create } from 'zustand'
import { api } from '../utils/api'
import { useToastStore } from './toastStore'

export type UserRole = 'admin' | 'team'

interface AuthState {
  token: string | null
  csrfToken: string | null
  role: UserRole | null
  activeAmpHost: string | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (password: string, turnstileToken: string) => Promise<boolean>
  logout: () => Promise<void>
  validateSession: () => Promise<void>
  refreshCSRF: () => Promise<string | null>
  updateCSRF: (newToken: string) => void
}

const storage = {
  getItem: (key: string): string | null => {
    try { return window.localStorage?.getItem(key) || null } catch { return null }
  },
  setItem: (key: string, value: string): void => {
    try { window.localStorage?.setItem(key, value) } catch {}
  },
  removeItem: (key: string): void => {
    try { window.localStorage?.removeItem(key) } catch {}
  }
}

const parseRole = (value: string | null): UserRole | null => {
  return value === 'admin' || value === 'team' ? value : null
}

const clearAll = () => {
  storage.removeItem('vendetta-token')
  storage.removeItem('vendetta-csrf')
  storage.removeItem('vendetta-role')
  storage.removeItem('vendetta-amp-host')
}

export const useAuthStore = create<AuthState>((set, get) => ({
  token: storage.getItem('vendetta-token'),
  csrfToken: storage.getItem('vendetta-csrf'),
  role: parseRole(storage.getItem('vendetta-role')),
  activeAmpHost: storage.getItem('vendetta-amp-host'),
  isAuthenticated: !!storage.getItem('vendetta-token'),
  isLoading: false,

  login: async (password: string, turnstileToken: string) => {
    set({ isLoading: true })

    try {
      const response = await api.post<{
        token: string; csrfToken?: string; role?: string; activeAmpHost?: string | null
      }>('/api/login', { password, turnstileToken })

      if (response.success && response.data?.token) {
        const token = response.data.token
        const csrfToken = response.data.csrfToken ?? null
        const role: UserRole = response.data.role === 'team' ? 'team' : 'admin'
        const activeAmpHost = response.data.activeAmpHost ?? null

        storage.setItem('vendetta-token', token)
        storage.setItem('vendetta-role', role)
        if (csrfToken) storage.setItem('vendetta-csrf', csrfToken)
        else storage.removeItem('vendetta-csrf')
        if (activeAmpHost) storage.setItem('vendetta-amp-host', activeAmpHost)
        else storage.removeItem('vendetta-amp-host')

        set({
          token,
          csrfToken,
          role,
          activeAmpHost,
          isAuthenticated: true,
          isLoading: false,
        })

        useToastStore.getState().addToast({
          type: 'success',
          message: role === 'admin' ? 'Admin login successful' : 'Team login successful',
        })

        return true
      } else {
        throw new Error(response.error || 'Login failed')
      }
    } catch (error) {
      console.error('Login error:', error)
      set({ isLoading: false })
      useToastStore.getState().addToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Login failed',
      })
      return false
    }
  },

  logout: async () => {
    const { token, csrfToken, isAuthenticated } = get()

    if (!isAuthenticated || !token) {
      clearAll()
      set({ token: null, csrfToken: null, role: null, activeAmpHost: null, isAuthenticated: false })
      return
    }

    set({ isAuthenticated: false })

    try {
      await api.post('/api/logout', {}, token, csrfToken || undefined)
    } catch (error) {
      console.log('Logout API call failed:', error)
    }

    clearAll()
    set({ token: null, csrfToken: null, role: null, activeAmpHost: null })

    useToastStore.getState().addToast({
      type: 'success',
      message: 'Logged out successfully',
    })
  },

  refreshCSRF: async () => {
    const { token, role } = get()
    if (!token || role !== 'admin') return null // team has no CSRF

    try {
      const response = await api.post<{ csrfToken?: string }>('/api/auth/csrf-refresh', {}, token)
      if (response.success && response.data?.csrfToken) {
        const newCsrfToken = response.data.csrfToken
        storage.setItem('vendetta-csrf', newCsrfToken)
        set({ csrfToken: newCsrfToken })
        return newCsrfToken
      }
      return null
    } catch (error) {
      console.error('CSRF refresh failed:', error)
      return null
    }
  },

  updateCSRF: (newToken: string) => {
    if (newToken) {
      storage.setItem('vendetta-csrf', newToken)
      set({ csrfToken: newToken })
    }
  },

  validateSession: async () => {
    const { token } = get()

    if (!token) {
      set({ isAuthenticated: false, role: null, isLoading: false })
      return
    }

    set({ isLoading: true })

    try {
      const response = await api.get<{
        authenticated?: boolean; role?: string; csrfToken?: string; activeAmpHost?: string | null
      }>('/api/auth/status', token)

      if (response.success && response.data?.authenticated) {
        const role: UserRole = response.data.role === 'team' ? 'team' : 'admin'
        const csrfToken = response.data.csrfToken ?? null
        const activeAmpHost = response.data.activeAmpHost ?? null

        storage.setItem('vendetta-role', role)
        if (csrfToken) storage.setItem('vendetta-csrf', csrfToken)
        if (activeAmpHost) storage.setItem('vendetta-amp-host', activeAmpHost)
        else storage.removeItem('vendetta-amp-host')

        set({
          role,
          csrfToken: csrfToken ?? get().csrfToken,
          activeAmpHost,
          isAuthenticated: true,
          isLoading: false,
        })
        return
      }
      throw new Error('Session invalid')
    } catch (error) {
      console.error('Session validation failed:', error)
      clearAll()
      set({
        token: null,
        csrfToken: null,
        role: null,
        activeAmpHost: null,
        isAuthenticated: false,
        isLoading: false,
      })
    }
  },
}))
